// src/components/publishing/draft-composer.tsx
import { useEffect, useState, useMemo } from "react";
import { Sparkles, Settings2 } from "lucide-react";
import {
  Destination, DESTINATIONS, destinationLabel, Integration,
} from "@/app/services/publishing/publishing-api";
import { Banner, Button, Checkbox, Textarea } from "../kit";
import { DEST_META, DestinationMark } from "@/app/components/shared/advanced-vocabulary";
import { getPublisherContext } from "@/api/content";
import type { PublisherContext } from "@/api/content";

interface DraftComposerProps {
  integrations: Integration[];
  brandName?: string;
  onDraft: (params: {
    brief: string;
    destinations: Destination[];
    tone?: string;
    /** #132: write from this action's brief (null: a free-text brief). */
    actionId?: number | null;
    customerId?: number | null;
  }) => Promise<void>;
  drafting: boolean;
  /**
   * Called when the user clicks the "Go to Integrations" CTA (shown when
   * one or more selected destinations aren't connected yet). The parent
   * should route the user to the Integrations page.
   */
  onNavigateToIntegrations?: () => void;
}

const TONE_PRESETS = [
  { id: "",            label: "Default (brand voice)" },
  { id: "analytical",  label: "Analytical" },
  { id: "punchy",      label: "Punchy & conversational" },
  { id: "authoritative", label: "Authoritative & thought-leadery" },
  { id: "story",       label: "Story-driven" },
];

export function DraftComposer({
  integrations, brandName, onDraft, drafting, onNavigateToIntegrations,
}: DraftComposerProps) {
  const [brief, setBrief] = useState("");
  const [selected, setSelected] = useState<Set<Destination>>(new Set());
  const [tone, setTone] = useState<string>("");
  // #132: the composer starts from a brief. An action's brief carries the
  // evidence; free text is still allowed, marked "no action, no evidence".
  const [context, setContext] = useState<PublisherContext | null>(null);
  const [actionId, setActionId] = useState<number | null>(null);

  useEffect(() => {
    setContext(null);
    setActionId(null);
    if (!brandName) return;
    getPublisherContext(brandName).then(setContext).catch(() => setContext(null));
  }, [brandName]);

  const connectedDestinations = useMemo(
    () => new Set(integrations.filter(i => i.status === "active").map(i => i.destination)),
    [integrations],
  );

  const canDraft = brief.trim().length >= 5 && selected.size > 0 && !drafting;

  const toggle = (d: Destination) => {
    const next = new Set(selected);
    if (next.has(d)) next.delete(d); else next.add(d);
    setSelected(next);
  };

  const handleDraft = async () => {
    if (!canDraft) return;
    await onDraft({
      brief: brief.trim(),
      destinations: Array.from(selected),
      tone: tone || undefined,
      actionId,
      customerId: context?.customer_id ?? null,
    });
  };

  const disconnectedSelected = Array.from(selected).filter(d => !connectedDestinations.has(d));

  return (
    <>
      <div className="upm-pub__field">
        <label className="upm-adv-eyebrow" htmlFor="pub-start">
          Start from
        </label>
        {context && context.customer_id != null && context.actions.length > 0 ? (
          <select
            id="pub-start"
            value={actionId ?? ""}
            onChange={e => {
              const id = e.target.value ? Number(e.target.value) : null;
              setActionId(id);
              const picked = context.actions.find(a => a.id === id);
              if (picked && !brief.trim()) setBrief(picked.title);
            }}
            disabled={drafting}
            style={{
              width: "100%", padding: "8px 10px", borderRadius: 9, border: "1px solid var(--border)",
              backgroundColor: "var(--background)", fontSize: 13, color: "var(--foreground)",
            }}
          >
            <option value="">A free-text brief (no action, no evidence)</option>
            {context.actions.map(a => (
              <option key={a.id} value={a.id}>{a.title}{a.belief ? ` · ${a.belief}` : ""}</option>
            ))}
          </select>
        ) : null}
        <p className="upm-pub__hint" style={actionId ? undefined : { color: "#B45309" }}>
          {context?.customer_id == null
            ? (context?.note || "This brand has no brand record: drafts are written without evidence and their claims are not checked.")
            : actionId
              ? "Written from this action's brief: the belief's figures, AI quotes, confirmed facts and voice. Every claim is checked against the brand record."
              : "No action, no evidence. The brand record's facts and voice still apply, and every claim is checked against it."}
        </p>
      </div>

      <div className="upm-pub__field">
        <label className="upm-adv-eyebrow" htmlFor="pub-brief">
          What do you want to publish about?
        </label>
        <Textarea
          id="pub-brief"
          value={brief}
          onChange={e => setBrief(e.target.value)}
          placeholder={
            brandName
              ? `e.g. "Announce our new integration with Salesforce. Focus on why we built it and what customers can automate now."`
              : `e.g. "Launch post for our new feature..."`
          }
          rows={5}
          state={drafting ? "disabled" : undefined}
        />
        <p className="upm-pub__hint">
          Be specific about the angle. The AI will adapt length and format per destination.
        </p>
      </div>

      <div className="upm-pub__row">
        <span className="upm-adv-eyebrow">Tone</span>
        {TONE_PRESETS.map(t => (
          <button
            key={t.id || "default"}
            type="button"
            className="upm-pub__tone"
            aria-pressed={tone === t.id}
            disabled={drafting}
            onClick={() => setTone(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="upm-pub__picker">
        <span className="upm-adv-eyebrow">Publish to</span>
        {DESTINATIONS.map(d => (
          <DestinationCheckbox
            key={d}
            destination={d}
            checked={selected.has(d)}
            connected={connectedDestinations.has(d)}
            onToggle={() => toggle(d)}
          />
        ))}
        {disconnectedSelected.length > 0 && (
          <Banner tone="warn">
            <span className="upm-lead">
              {disconnectedSelected.length === 1
                ? `${destinationLabel(disconnectedSelected[0])} is not connected`
                : "Some destinations are not connected"}
            </span>
            <>
              You can still draft content, but publishing will fail for a destination that is not
              connected. Connect it on the Integrations page first.
              {onNavigateToIntegrations && (
                <div style={{ marginTop: "var(--sp-2)" }}>
                  <Button variant="secondary" icon={Settings2} onClick={onNavigateToIntegrations}>
                    Go to Integrations
                  </Button>
                </div>
              )}
            </>
          </Banner>
        )}
      </div>

      <div className="upm-pub__foot">
        <span className="upm-pub__hint">
          {canDraft
            ? "One draft per destination, in that destination's own voice."
            : "Write at least a sentence and pick a destination."}
        </span>
        <span className="upm-pub__cta">
          <Button
            icon={Sparkles}
            onClick={handleDraft}
            disabled={!canDraft}
            loading={drafting}
            loadingLabel="Drafting"
          >
            Write the drafts
          </Button>
        </span>
      </div>
    </>
  );
}

// ─── Destination checkbox row ────────────────────────────────────────────────

function DestinationCheckbox({
  destination, checked, connected, onToggle,
}: {
  destination: Destination;
  checked: boolean;
  connected: boolean;
  onToggle: () => void;
}) {
  const meta = DEST_META[destination];

  /* Connection state GATES publishing, so a destination that is not connected
     cannot look like one that is. `data-connected` carries it on the row and
     the state is also stated in words — a colour alone is not a label. */
  return (
    <button
      type="button"
      className="upm-pub__pick"
      aria-pressed={checked}
      data-connected={connected}
      onClick={onToggle}
    >
      {/* The checkbox leads, because the question the row asks is "is this one
          in?" and the answer has to be readable before the name. */}
      <Checkbox
        checked={checked}
        label={checked ? `Remove ${destinationLabel(destination)}` : `Add ${destinationLabel(destination)}`}
        labelHidden
        onChange={onToggle}
      />
      <span className="upm-pub__pickmark" aria-hidden="true">
        <DestinationMark id={destination} />
      </span>
      <span className="upm-pub__pickbody">
        <span className="upm-pub__pickname" style={{ display: "block" }}>
          {destinationLabel(destination)}
        </span>
        <span className="upm-pub__pickmeta">
          {meta.blurb}
          {" · "}
          <span
            className={connected ? "upm-dest-row__state--on" : "upm-dest-row__state--off"}
            style={{ fontWeight: "var(--fw-semibold)" }}
          >
            {connected ? "Connected" : "Not connected"}
          </span>
        </span>
      </span>
    </button>
  );
}
